"use client";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import cn from "classnames";
import Image from "next/image";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useEffect, useMemo } from "react";

import { ProviderHooks, StyleHooks } from "@/src/hook";
import { AvailableHomesLogic } from "@/src/logic";
import { StrapiComponentType, ComponentType, StrapiCollectionType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import { SectionModule, TextBreakpointModule } from "@/src/component/partial/__module__";
import AvailableHomesButtons from "./AvailableHomesButtons";
import AvailableHomesCard from "./AvailableHomesCard";

import styles from "./AvailableHomes.module.scss";
import { Typography } from "@mui/material";

const breakpoints = {
  1025: {
    slidesPerView: 3,
  },
  200: {
    slidesPerView: 1.11,
  },
};

interface AvailableHomesProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.AvailableHomes> {}

function AvailableHomes({
  section,
  slug,
  snapToGrid,
  classes,
  searchParams,
  quality,
  mobileHint,
  userAgent,
  deviceWidthHint,
  layout,
  lazy,
}: AvailableHomesProps) {
  const VERY_HIGH = 99999999999;
  const isSmall = StyleHooks.useBreakPointDown("sm");
  const propertyUnit: StrapiComponentType.Section.AvailableHomes = section.type?.[0];
  const { floorplans, filters, registerHomeCount, homesCount, filtersQuery } = ProviderHooks.useProperty();
  const id = `ahomeid-${propertyUnit.id}`;
  const pagination = `available-homes-page-${id}`;
  const prevButton = `available-homes-prev_${id}`;
  const nextButton = `available-homes-next_${id}`;
  const typeOfLayout = propertyUnit.layout ?? "carousel";
  const filterType = propertyUnit?.filters?.[0]?.filterType;

  //this allows the cards to always be centered on mobile in loop ->
  //with a equal portion of another card on each side
  const cards: StrapiCollectionType.PropertyFloorplan[] = useMemo(() => floorplans ?? [], [floorplans]);

  //this is a simple filter to get started, we should make this more robust
  const simpleFilter = AvailableHomesLogic.simpleFilter(cards, filterType ?? "");

  //this applies the user selected filters to the simple filter
  const addUserFilters = AvailableHomesLogic.userFilter(simpleFilter, filters);

  useEffect(() => {
    const id = `c_${propertyUnit.id}`;
    if (homesCount[id] === addUserFilters.length) {
      return;
    }

    registerHomeCount(`c_${propertyUnit.id}`, addUserFilters.length);
  }, [addUserFilters, propertyUnit.id, homesCount, registerHomeCount]);

  /**
   * Allow for rendered cards to be passed to different layouts in return elements
   */
  const memoizedCards = useMemo(() => {
    const sortedByPrice = (addUserFilters ?? []).sort((a, b) => (a.startingAtPrice ?? VERY_HIGH) - (b.startingAtPrice ?? VERY_HIGH));
    return sortedByPrice.map((card, i) => (
      <SwiperSlide
        key={`${card.id}_${i}`}
        className={cn(styles.card, typeOfLayout !== "carousel" ? styles.fullWidth : "")}
      >
        <AvailableHomesCard
          card={card}
          quality={quality}
          lazy="eager"
          index={i}
          mobileHint={mobileHint}
          total={(cards ?? []).length}
          deviceWidthHint={deviceWidthHint}
          backgroundImg={propertyUnit.dollhouseBackground?.data?.attributes?.url}
          cta={propertyUnit.cta}
          layout={layout}
          unitLinkActive={propertyUnit.unitLinkActive}
          swiperOn={typeOfLayout === "carousel"}
          filtersQuery={filtersQuery}
        />
      </SwiperSlide>
    ));
  }, [cards, quality, mobileHint, deviceWidthHint, propertyUnit, layout, addUserFilters, typeOfLayout, filtersQuery]);

  /**
   * Get the CSS for the type of layout for the section title container
   */
  const getTypeLayoutCss = () => {
    if (typeOfLayout === "gridMax2x2") {
      return styles.gridMax2x2;
    } else if (typeOfLayout === "gridMax3x3") {
      return styles.gridMax3x3;
    }
    return styles.swiper;
  };

  if (!memoizedCards.length) {
    return <></>;
  }

  const centerSlides = isSmall || mobileHint ? true : memoizedCards.length > 3;
  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: cn(classes?.section, !snapToGrid ? styles.section : undefined),
        children: cn(snapToGrid ? styles.snapToGrid : styles.noGrid, styles.sectionChildren),
        styledContainer: cn(snapToGrid ? styles.snapToGrid : styles.noGrid, styles.container),
      }}
      stylePrefix="PropertyUnit"
      styleContainerId={propertyUnit.id}
      snapToGrid={snapToGrid}
      searchParams={searchParams}
      quality={quality}
      layout={layout}
      lazy={lazy}
    >
      {(cards ?? []).length > 3 && typeOfLayout === "carousel" && (
        <AvailableHomesButtons
          prevClassName={prevButton}
          nextClassName={nextButton}
          classes={{
            prevClassName: styles.buttonPrev,
            nextClassName: styles.buttonNext,
          }}
        />
      )}
      <TextBreakpointModule
        id={`property_unit_title_${propertyUnit.id}`}
        text={section.title}
        className={styles.title}
      />
      {propertyUnit.title && (
        <div className={cn(styles.sectionTitleWithIcon, getTypeLayoutCss())}>
          {propertyUnit?.titleIcon?.data?.attributes?.url && (
            <Image
              src={propertyUnit.titleIcon.data.attributes.url}
              alt={propertyUnit.title}
              width={propertyUnit?.titleIconWidth ?? propertyUnit.titleIcon.data.attributes.width ?? 0}
              height={propertyUnit?.titleIconHeight ?? propertyUnit.titleIcon.data.attributes.height ?? 0}
              loader={LoadFromCDN.loaderForNextJS}
            />
          )}
          <Typography className={styles.titleWithIcon}>
            {(addUserFilters ?? []).length} {propertyUnit.title}
          </Typography>
        </div>
      )}
      {typeOfLayout === "carousel" && (
        <Swiper
          key={`${memoizedCards.length}-${id}`}
          id={`swiper_${id}`}
          className={styles.swiper}
          wrapperClass={styles.wrapper}
          modules={[Navigation, Pagination]}
          centerInsufficientSlides={centerSlides}
          centeredSlides={centerSlides}
          centeredSlidesBounds={centerSlides}
          loop={centerSlides}
          spaceBetween={0}
          slidesPerView={3}
          breakpoints={breakpoints}
          direction="horizontal"
          navigation={{
            prevEl: `#${prevButton}`,
            nextEl: `#${nextButton}`,
          }}
          userAgent={userAgent}
        >
          {memoizedCards}
          <div className={cn(pagination, styles.pagination)}></div>
        </Swiper>
      )}
      {typeOfLayout === "gridMax2x2" && <div className={styles.gridMax2x2}>{memoizedCards}</div>}
      {typeOfLayout === "gridMax3x3" && <div className={styles.gridMax3x3}>{memoizedCards}</div>}
    </SectionModule>
  );
}

export default AvailableHomes;
